import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AlertPopup from "components/alert-popup/AlertPopup";
import Button from "core/buttons/button/Button";
import { HandleModaleBO } from "components/bo/HandleModaleBO";
import { TableManager } from "components/bo/admin-table/TableManager";
import { useJobCategoryContext } from "api/settings/job-category/JobCategoryProvider";
import { useJobContext } from "api/settings/job/JobProvider";
import { useUserContext } from "api/user/UserProvider";
import {
  ACTIONS,
  ENTITIES,
  POPUP_STATUS,
  QUERY_STATUS,
  VAR_TYPES,
} from "constants/constants";
import { GENERIC_ERROR } from "constants/popup-codes";
import { getTranslatedList } from "utils/helper";
import {
  joinOptionLabels,
  navigateEdit,
  navigateToParent,
  navigateView,
  optionsFromIds,
} from "utils/boHelper";

// No functional ceiling on how many jobs a category holds. InputSearchPopup
// allows limitChoice + 1 selections and InputSearchTag only renders the first
// limitChoice tags, so this stays well above any referential size rather than
// tracking it (same approach as HF-0076).
const MAX_JOBS_PER_CATEGORY = 999;

export default function AdminJobCategory({ action }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { lang } = useUserContext();
  const { jobList, jobListGetter } = useJobContext();
  const {
    apiErrorResponse,
    jobCategoryByIdGetter,
    jobCategoryCreate,
    jobCategoryDeleteById,
    jobCategoryList,
    jobCategoryListGetter,
    jobCategoryPerId,
    jobCategoryStatus,
    jobCategoryUpdateById,
    setJobCategoryStatus,
  } = useJobCategoryContext();

  const [deleteData, setDeleteData] = useState(null);
  const [popupCode, setPopupCode] = useState();

  useEffect(() => {
    jobCategoryListGetter();
    jobListGetter();
  }, [jobCategoryListGetter, jobListGetter]);

  useEffect(() => {
    if (id) {
      jobCategoryByIdGetter(id);
    }
  }, [id, jobCategoryByIdGetter]);

  // Admin screens have no error feedback of their own: business refusals
  // (protected category, category still holding jobs) are surfaced here.
  useEffect(() => {
    if (jobCategoryStatus !== QUERY_STATUS.ERROR) return;
    const [firstError] = Object.values(apiErrorResponse || {});
    setPopupCode(firstError?.translationKey || GENERIC_ERROR);
    setJobCategoryStatus(QUERY_STATUS.INITIAL);
  }, [jobCategoryStatus, apiErrorResponse, setJobCategoryStatus]);

  const jobOptions = useMemo(
    () => getTranslatedList({ lang, list: jobList }) || [],
    [lang, jobList]
  );

  const selectedJobOptions = useCallback(
    (jobIds) => optionsFromIds({ ids: jobIds, options: jobOptions }),
    [jobOptions]
  );

  // Guards against acting on the previously visited category: the provider
  // keeps it until the current one has loaded.
  const isCurrentCategory = !!id && Number(id) === jobCategoryPerId?.id;
  const isProtected = isCurrentCategory && !!jobCategoryPerId?.is_protected;

  const jobCategoryForm = useMemo(
    () => [
      { col: "fr", mandatory: true, isMissingValue: null, disabled: isProtected },
      { col: "en", mandatory: true, isMissingValue: null, disabled: isProtected },
      {
        col: "jobs",
        mandatory: false,
        isMissingValue: null,
        type: VAR_TYPES.TAGS,
        limitChoice: MAX_JOBS_PER_CATEGORY,
        choices: jobOptions,
        popup: {
          title: {
            id: "admin_job_category_jobs_search",
            defaultMessage: "Rechercher et sélectionner un métier",
          },
        },
      },
    ],
    [isProtected, jobOptions]
  );

  // Withheld until the job list can resolve the stored links, otherwise the
  // form would prefill with unresolved ids and save them back as labels.
  const isFormEntityReady =
    isCurrentCategory &&
    (jobOptions.length > 0 || !(jobCategoryPerId?.jobs || []).length);

  const formEntity = useMemo(
    () =>
      isFormEntityReady
        ? {
            ...jobCategoryPerId,
            jobs: selectedJobOptions(jobCategoryPerId?.jobs),
          }
        : {},
    [isFormEntityReady, jobCategoryPerId, selectedJobOptions]
  );

  const formatJobCategory = useCallback(
    (jobCategory) => ({
      id: jobCategory?.id,
      fr: jobCategory?.fr,
      en: jobCategory?.en,
      jobs: joinOptionLabels(selectedJobOptions(jobCategory?.jobs)),
    }),
    [selectedJobOptions]
  );

  const formattedJobCategoryList = useMemo(
    () =>
      jobCategoryList.map((jobCategory) => ({
        ...jobCategory,
        jobs_count: (jobCategory?.jobs || []).length,
      })),
    [jobCategoryList]
  );

  // Whitelisted: formValues also carries the fields returned by the GET, and
  // is_protected in particular must never travel back to the server.
  const formatPayload = (payload) => ({
    fr: payload?.fr,
    en: payload?.en,
    jobs: (payload?.jobs || []).map((job) => job.value),
  });

  // Navigation only happens once the mutation resolved, so a business refusal
  // keeps the user on the form with the popup shown.
  const handleCreate = async (payload) => {
    try {
      await jobCategoryCreate(formatPayload(payload));
      navigateToParent(navigate, location);
    } catch {
      // Surfaced by the popup effect above.
    }
  };

  const handleEdit = async (payload) => {
    try {
      await jobCategoryUpdateById(payload.id, formatPayload(payload));
      navigateToParent(navigate, location);
    } catch {
      // Surfaced by the popup effect above.
    }
  };

  const handleDelete = async (jobCategoryId) => {
    const jobCategory = jobCategoryList.find((e) => e.id === jobCategoryId);
    if (jobCategory) setDeleteData(formatJobCategory(jobCategory));
  };

  const validateDelete = async (jobCategoryId) => {
    try {
      await jobCategoryDeleteById(jobCategoryId);
    } catch {
      // Surfaced by the popup effect above.
    }
  };

  return (
    <div>
      <AlertPopup
        code={popupCode}
        status={popupCode ? POPUP_STATUS.ERROR : undefined}
        setCode={setPopupCode}
        setStatus={() => setPopupCode()}
      />
      {action === ACTIONS.LIST && (
        <div className="flex">
          <Button
            className="btn-hover-color"
            size="large"
            color="blue"
            onClick={() => navigateView("new", navigate, location)}
          >
            <FormattedMessage
              id={"admin_create_new"}
              defaultMessage={"Nouveau"}
            />
          </Button>
        </div>
      )}
      {isProtected && (
        <p>
          <FormattedMessage
            id={"admin_job_category_protected_msg"}
            defaultMessage={
              "Cette catégorie est protégée : son libellé ne peut pas être modifié et elle ne peut pas être supprimée. Les métiers rattachés restent modifiables."
            }
          />
        </p>
      )}
      <br />
      <TableManager
        action={action}
        entity={ENTITIES.JOB_CATEGORIES}
        entityPerId={formEntity}
        formattedEntity={formatJobCategory(
          isCurrentCategory ? jobCategoryPerId : {}
        )}
        list={formattedJobCategoryList}
        inputs={jobCategoryForm}
        isRowDeletable={(jobCategory) => !jobCategory?.is_protected}
        navigateEdit={(propsId) => navigateEdit(propsId, navigate, location)}
        navigateView={(propsId) => navigateView(propsId, navigate, location)}
        handleCreate={handleCreate}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
      />
      <div id="modalDelete">
        <HandleModaleBO
          setDeleteData={setDeleteData}
          validateDelete={validateDelete}
          deleteData={deleteData}
          entity={ENTITIES.JOB_CATEGORIES}
        />
      </div>
    </div>
  );
}

AdminJobCategory.propTypes = {
  action: PropTypes.string,
};
